import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import { PeopleRecommendation } from "@mirage/shared/search/people-recommendations";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import * as rx from "rxjs";
import { v5 as uuidv5 } from "uuid";
import * as sourceScore from "../scoring/source-score";
import { twoStageSearch } from "../search/two-stage-search";
import { getResultSection } from "../utils/section-mapper";
import { getSourceLimit } from "../utils/source-helpers";

const PEOPLE_FILTER_RESULT_NAMESPACE = "21791866-0d0d-4690-94f4-11908f263cb4";
const FILTER_RESULT_LIMIT = 5000;

export const raw: typeahead.TaggedSearch = (
  query,
  mode,
  { cache, config, grossLimit, sectionLimit },
) => {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.PeopleFilter,
    query,
    mode,
    config,
    grossLimit,
    sectionLimit,
  );

  const sectionId = getResultSection(
    typeahead.ResultType.PeopleFilter,
    mode,
    query,
    config,
  );

  return rx.defer(() => {
    const peopleRecommendations = cache.all(
      CacheKey.PeopleRecommendations,
    ) as PeopleRecommendation[];

    return rx.of(peopleRecommendations).pipe(
      rx.map((allPeopleRecommendations) => {
        const candidates = twoStageSearch<PeopleRecommendation>(
          query,
          allPeopleRecommendations.map((peopleRecommendation) => ({
            searchableStrings: [
              ...(peopleRecommendation.displayName
                ? [
                    ...peopleRecommendation.displayName.split(" "),
                    peopleRecommendation.displayName,
                  ]
                : []),
              peopleRecommendation.email,
            ].filter(isDefined),
            originalResult: peopleRecommendation,
          })),
          { limit: FILTER_RESULT_LIMIT, isPeopleSearch: true },
        ).map((searchableResult) => searchableResult.originalResult);

        const taggedResults = candidates.map((candidate) => {
          const peopleFilterUuid = uuidv5(
            candidate.uuid,
            PEOPLE_FILTER_RESULT_NAMESPACE,
          );
          return primitives.peopleFilter(peopleFilterUuid, candidate, {
            sectionId,
          });
        });

        return taggedResults
          .map((taggedResult) => sourceScore.score(query, config)(taggedResult))
          .sort((a, b) => b.score - a.score)
          .slice(0, sourceLimit);
      }),
    );
  });
};

export const search = wrappers.wrapped(SourceId.AskSuggestions, raw);
